import { useCallback, useEffect, useRef } from 'react';

import { useRepeatableFunction } from './useRepeatableFunction';

export interface IUseNavigationKeysOptions {
  enableArrowKeys?: boolean;
  enableRepeat?: boolean;
  initialDelay?: number;
  repeatInterval?: number;
  element?: React.RefObject<HTMLElement>;
}

export interface IUseNavigationKeysReturn {
  startPrevious: () => void;
  stopPrevious: () => void;
  startNext: () => void;
  stopNext: () => void;
}

/**
 * Hook that provides navigation functionality with arrow keys and hold-to-repeat.
 * Combines keyboard navigation with repeatable function calls.
 *
 * @param onPrevious - Function to call when navigating to previous item
 * @param onNext - Function to call when navigating to next item
 * @param options - Configuration options
 * @param options.enableArrowKeys - Enable arrow key navigation (default: false)
 * @param options.enableRepeat - Enable hold-to-repeat functionality (default: true)
 * @param options.initialDelay - Delay before starting repetition (default: 800ms)
 * @param options.repeatInterval - Interval between repetitions (default: 300ms)
 * @param options.element - Element to attach keyboard listeners to (required if enableArrowKeys is true)
 * @returns Object with start/stop methods for previous and next navigation
 */
export function useNavigationKeys(
  onPrevious: () => void,
  onNext: () => void,
  options: IUseNavigationKeysOptions = {},
): IUseNavigationKeysReturn {
  const {
    enableArrowKeys = false,
    enableRepeat = true,
    initialDelay = 800,
    repeatInterval = 300,
    element,
  } = options;

  // Store the latest handlers in refs to avoid stale closures
  const onPreviousRef = useRef(onPrevious);
  const onNextRef = useRef(onNext);

  useEffect(() => {
    onPreviousRef.current = onPrevious;
    onNextRef.current = onNext;
  }, [onPrevious, onNext]);

  // Wrapper functions that call the current handlers
  const handlePrevious = useCallback(() => onPreviousRef.current(), []);
  const handleNext = useCallback(() => onNextRef.current(), []);

  // Set up repeatable functionality
  const previousRepeater = useRepeatableFunction(handlePrevious, {
    enabled: enableRepeat,
    initialDelay,
    repeatInterval,
  });

  const nextRepeater = useRepeatableFunction(handleNext, {
    enabled: enableRepeat,
    initialDelay,
    repeatInterval,
  });

  // Keyboard event handler
  const handleKeyDown = useCallback(
    (event: KeyboardEvent): void => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNext();
      }
    },
    [handlePrevious, handleNext],
  );

  // Set up keyboard listeners
  useEffect(() => {
    if (!enableArrowKeys || !element?.current) {
      return;
    }

    const elementRef = element.current;
    elementRef.addEventListener('keydown', handleKeyDown);

    return () => {
      elementRef.removeEventListener('keydown', handleKeyDown);
    };
  }, [enableArrowKeys, element, handleKeyDown]);

  return {
    startPrevious: previousRepeater.start,
    stopPrevious: previousRepeater.stop,
    startNext: nextRepeater.start,
    stopNext: nextRepeater.stop,
  };
}
